<template>
  <v-card v-if="loaded" class="mt-3" color="blue-grey lighten-5" elevation="0">
    <v-card-text>
      <v-row>
        <v-col lg="3" md="6" cols="12">
          <v-card elevation="1" height="300">
            <v-card-title class="text-h5">
              {{ $t('performance_dashboard_ui.overall_fleet_health') }}
              <info-modal width="800">
                <v-card-title class="text-h5">
                  {{ $t('performance_dashboard_ui.modal_info.fleet_health.main.title') }}
                </v-card-title>
                <v-card-text v-html="fleetHealthModalText"/>
              </info-modal>
            </v-card-title>
            <v-card-subtitle>
              ({{ $t('as_of_today') }})
            </v-card-subtitle>
            <v-divider></v-divider>
            <v-card-text>
              <div class="text-subtitle-1" v-if="overallStatus.totalVehicles === 0">
                {{ $t('no_vehicles') }}
              </div>
              <v-list v-else class="py-0">
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-truck-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('healthy') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="success" dark>
                      {{ overallStatus.healthy.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-alert</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('unhealthy') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="danger" dark>
                      {{ overallStatus.unhealthy.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>mdi-eye-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    {{ $t('ignored') }}
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-chip color="blue-grey" dark>
                      {{ overallStatus.ignored.toLocaleString() }}
                    </v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col>
          <v-card elevation="1" height="300">
            <v-card-title class="text-h5 d-flex justify-space-between">
              <div class="d-flex align-center">
                <span>
                  {{ $t('performance_dashboard_ui.daily_trend') }}
                  <span class="six-months-subheader hidden-md-and-down">{{ $t('performance_dashboard_ui.trailing_six_months') }}</span>
                </span>
                <info-modal width="800">
                  <v-card-title class="text-h5">
                    {{ $t('performance_dashboard_ui.modal_info.fleet_health.six_month_trend.title') }}
                  </v-card-title>
                  <v-card-text v-html="$t('performance_dashboard_ui.modal_info.fleet_health.six_month_trend.text')"/>
                </info-modal>
              </div>
              <v-btn color="primary" @click="generateFleetHealthReport">
                <v-icon>mdi-chart-areaspline</v-icon>
                {{ $t('performance_dashboard_ui.see_full_report') }}
              </v-btn>
            </v-card-title>
            <v-card-text>
              <trailing-fleet-health-chart ref="trailing-health-chart" :selected-customers="selectedCustomers" :height="210"/>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-card v-if="filteredCustomers.length > 1" class="mt-4" elevation="1">
        <v-card-title class="text-h5">
          {{ $t('performance_dashboard_ui.dc_fleet_health') }}
          <info-modal width="800">
            <v-card-title class="text-h5">
              {{ $t('performance_dashboard_ui.modal_info.fleet_health.distribution_centers.title') }}
            </v-card-title>
            <v-card-text v-html="$t('performance_dashboard_ui.modal_info.fleet_health.distribution_centers.text')"/>
          </info-modal>
        </v-card-title>
        <v-card-subtitle class="pb-0">
          ({{ $t('as_of_today') }})
        </v-card-subtitle>
        <v-divider></v-divider>
        <v-data-table
          class="vf-table"
          :items="filteredCustomers"
          :headers="tableFields"
          sort-by="percent_healthy">
          <template #item.name="row">
            <router-link :to="`/performance/dashboard/customers/${row.item.id}`">{{ row.item.name }}</router-link>
          </template>
          <template #item.percent_healthy="row">
            {{ round(row.item.percent_healthy, 1) }}%
          </template>
          <template #item.num_healthy="row">
            {{ row.item.num_healthy.toLocaleString() }}
          </template>
          <template #item.num_unhealthy="row">
            {{ row.item.num_unhealthy.toLocaleString() }}
          </template>
        </v-data-table>
      </v-card>
    </v-card-text>
  </v-card>
  <v-card v-else class="mt-3" color="blue-grey lighten-5" elevation="0">
    <v-card-text>
      <v-row>
        <v-col lg="3" md="6" cols="12">
          <v-skeleton-loader type="card" />
        </v-col>
        <v-col>
          <v-skeleton-loader type="card" />
        </v-col>
      </v-row>
    </v-card-text>
    <v-skeleton-loader type="table"/>
  </v-card>
</template>

<script>
import TrailingFleetHealthChart from '@/components/performance-dashboard/TrailingFleetHealthChart'
import InfoModal from '@/components/shared/InfoModal'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import PerformanceDashboardComponentMixin from '@/components/performance-dashboard/PerformanceDashboardComponentMixin'
import MathUtil from '@/util/MathUtil'
import Moment from 'moment-timezone'
import { map, each, take, filter, invoke, round } from 'lodash'

export default {
  name: 'FleetHealthDashboard',
  components: {
    TrailingFleetHealthChart,
    InfoModal
  },
  mixins: [
    CurrentUserMixin,
    PerformanceDashboardComponentMixin,
    TemperatureMixin
  ],
  data () {
    return {
      loaded: false,
      customersHealthData: null,
      nominalCoolerTemperature: RouteViolationRecord.DEFAULT_NOMINAL_COOLER_TEMPERATURE,
      nominalFreezerTemperature: RouteViolationRecord.DEFAULT_NOMINAL_FREEZER_TEMPERATURE
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    take: take,
    round: round,
    /**
     * Generate the full Trailing 6 month Fleet Health report and redirect to it.
     */
    async generateFleetHealthReport () {
      const reportParameters = {}

      reportParameters.startDate = Moment.tz(this.customerTimezone).subtract(6, 'months').format('YYYY-MM-DD')
      reportParameters.endDate = Moment.tz(this.customerTimezone).format('YYYY-MM-DD')
      reportParameters.customerIds = map(this.selectedCustomers, x => x.id)

      const report = this.$orm('Report').createRecord({
        type: 'FleetHealthHistoryReport',
        name: this.$t('performance_dashboard_ui.fleet_health_history_report'),
        parameters: reportParameters
      })

      const savedReport = await this.$orm('Report').createOrUpdate(report)
      await this.$router.push(`/report/${savedReport.id}`)
    },
    /**
     * Load the fleet health data from the API
     */
    async loadData () {
      this.loaded = false
      await this.$emit('loading')
      try {
        const result = await this.$httpAdapter.get('/customer-insights/fleet-health', {
          params: { customer_id: this.requestedCustomerIds.join(',') }
        })
        this.customersHealthData = result.data
        this.loaded = true
        this.$nextTick(() => {
          this.$emit('loaded', {
            value: this.overallStatus.percentHealthy
          })
        })
      } catch (error) {
        this.$emit('errorLoading')
        this.handleError(error)
      }
    },
    /**
     * Given a percentage, return the defined associated health status:
     * > 90 is 'healthy'
     * < 90 but >= 80 is 'unhealthy'
     * < 80 is 'danger'
     */
    getHealthStatus (value) {
      if (value >= 90) {
        return 'healthy'
      }
      if (value < 90 && value >= 80) {
        return 'unhealthy'
      }
      return 'danger'
    }
  },
  computed: {
    tableFields () {
      return [
        {
          value: 'name',
          text: this.$tc('name', 2)
        },
        {
          value: 'percent_healthy',
          text: this.$t('performance_dashboard_ui.percent_healthy')
        },
        {
          value: 'num_healthy',
          text: this.$t('performance_dashboard_ui.healthy_vehicles')
        },
        {
          value: 'num_unhealthy',
          text: this.$t('unhealthy')
        },
        {
          value: 'num_ignored',
          text: this.$t('ignored')
        }
      ]
    },
    /**
     * The data object used for the 'Overall Health' section.
     * @returns Object|null
     */
    overallStatus () {
      if (this.loaded && this.customersHealthData != null) {
        const results = {
          totalVehicles: 0,
          healthy: 0,
          unhealthy: 0,
          ignored: 0,
          percentHealthy: 0
        }
        each(this.customersHealthData, (customer) => {
          const numVehicles = customer.num_healthy + customer.num_unhealthy
          results.totalVehicles += numVehicles
          results.healthy += customer.num_healthy
          results.unhealthy += customer.num_unhealthy
          results.ignored += customer.num_ignored
        })
        results.percentHealthy = MathUtil.percentage(results.healthy, results.totalVehicles, null)
        return results
      }
      return null
    },
    /**
     * A list of customers that:
     * 1) Has had customer ids and names added in (since these aren't returned with the API)
     * 2) Has customers with no vehicles filtered out
     * 3) Has been sorted by the health percentage
     */
    filteredCustomers () {
      const mapFn = (customer, customerId) => {
        customer.id = parseInt(customerId)
        customer.name = this.getCustomerName(customerId)
        customer.total = customer.num_healthy + customer.num_unhealthy
        // The API's percent_healthy is pre-rounded to 2dp; recompute it from the counts so the table rounds only once
        customer.percent_healthy = MathUtil.percentage(customer.num_healthy, customer.total, null)
        return customer
      }
      // Filter out all customers with no vehicles
      const filterFn = (customer) => customer.total > 0
      return filter(map(this.customersHealthData, mapFn), filterFn)
    },

    /**
     * Setting this as a computed property because it's slightly annoying to put it in the template as a v-html property
     * @returns String
     */
    fleetHealthModalText () {
      return this.$t('performance_dashboard_ui.modal_info.fleet_health.main.text', {
        coolerNominalTemp: this.temperatureToCustomerScale(this.nominalCoolerTemperature),
        freezerNominalTemp: this.temperatureToCustomerScale(this.nominalFreezerTemperature),
        tempFormat: this.customerContext.tempFormat()
      })
    }
  },
  watch: {
    selectedCustomers () {
      this.loadData()
    },
    // When tab becomes active, force the charts to redraw (in case the window size changed while on another tab)
    active (newValue, oldValue) {
      if (newValue === true) {
        invoke(this.$refs, 'trailing-health-chart.fit')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.six-months-subheader {
  font-weight: normal;
  font-size: 18px;
  opacity: .8;
}
</style>
